// =============================================================================
// features/sales/agenda/components/CancelAppointmentPanel.tsx — Cancelamento
// inline do drawer, com motivo obrigatório (AppointmentCancelBodySchema).
// =============================================================================

import type { Appointment } from '@elemento/shared-schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '../../../../components/ui/Button';
import { FormTextarea } from '../../components/FormTextarea';
import { useCancelAppointment } from '../hooks';

const CancelFormSchema = z.object({
  reason: z.string().min(1, 'Informe o motivo do cancelamento').max(2000, 'Máximo 2000 caracteres'),
});

type CancelFormValues = z.infer<typeof CancelFormSchema>;

export interface CancelAppointmentPanelProps {
  appointment: Appointment;
  onDone: () => void;
  onCancel: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

export function CancelAppointmentPanel({
  appointment,
  onDone,
  onCancel,
  onDirtyChange,
}: CancelAppointmentPanelProps): React.JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<CancelFormValues>({
    resolver: zodResolver(CancelFormSchema),
    defaultValues: { reason: '' },
  });

  React.useEffect(() => onDirtyChange(isDirty), [isDirty, onDirtyChange]);

  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const { mutate, isPending } = useCancelAppointment({
    onSuccess: () => {
      setSubmitError(null);
      onDone();
    },
    onError: (error) =>
      setSubmitError(error.message || 'Não foi possível cancelar. Tente novamente.'),
  });

  function onSubmit(values: CancelFormValues): void {
    setSubmitError(null);
    mutate({ id: appointment.id, body: { reason: values.reason.trim() } });
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <div
        role="alert"
        className="rounded-sm px-4 py-3 border-l-[3px]"
        style={{
          borderColor: 'var(--warning)',
          background: 'var(--warning-bg)',
          boxShadow: 'var(--elev-1)',
        }}
      >
        <p className="font-sans font-semibold text-sm" style={{ color: 'var(--warning)' }}>
          Cancelar este compromisso
        </p>
        <p className="font-sans text-xs text-ink-2 mt-0.5">
          Esta ação encerra o compromisso e não pode ser desfeita pela agenda.
        </p>
      </div>

      <FormTextarea
        id="cancel-reason"
        label="Motivo do cancelamento"
        required
        placeholder="Ex: cliente remarcou por telefone"
        disabled={isPending}
        error={errors.reason?.message}
        {...register('reason')}
      />

      {submitError && (
        <div
          role="alert"
          className="rounded-sm px-4 py-3 border-l-[3px]"
          style={{
            borderColor: 'var(--danger)',
            background: 'var(--danger-bg)',
            boxShadow: 'var(--elev-1)',
          }}
        >
          <p className="font-sans font-semibold text-sm text-danger">Erro ao cancelar</p>
          <p className="font-sans text-xs text-ink-2 mt-0.5">{submitError}</p>
        </div>
      )}

      <div className="flex gap-2.5">
        <Button
          type="submit"
          variant="danger"
          disabled={isPending}
          className="flex-1 justify-center"
        >
          {isPending ? 'Cancelando…' : submitError ? 'Tentar novamente' : 'Cancelar compromisso'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isPending}>
          Voltar
        </Button>
      </div>
    </form>
  );
}
